import React from 'react';
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  SimpleGrid,
  Center,
  Divider,
  List,
  ListItem,
  ListIcon,
  Badge,
  useColorModeValue,
  Stack,
  HStack,
  Icon,
  Flex,
} from '@chakra-ui/react';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const campaignImage = `https://${window.location.host}/images/share-image.png`;

const Pricing = () => {
  return (
    <Box bg={useColorModeValue('#f5f3ee', 'gray.900')} color="#0f393b">
      <Helmet>
        <title>Pricing - PurelyGive</title>
        <meta
          name="description"
          content="Discover how PurelyGive offers zero transaction fees for nonprofits. Learn about our transparent pricing and how we keep fundraising free."
        />
        <meta
          name="keywords"
          content="pricing, zero fees, no transaction fees, fundraising costs, PurelyGive"
        />
        <meta property="og:title" content="PurelyGive Pricing - Zero Transaction Fees" />
        <meta
          property="og:description"
          content="Learn how PurelyGive enables free fundraising with no transaction fees. Transparent pricing for nonprofits."
        />
        <meta property="og:image" content={campaignImage} />
        <meta property="og:url" content="https://purelygive.com/pricing" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="PurelyGive Pricing - Zero Transaction Fees" />
        <meta
          name="twitter:description"
          content="Transparent pricing for nonprofits: no platform fees, no contracts, and no setup costs. See how PurelyGive helps you keep more from every donation."
        />
        <meta name="twitter:image" content={campaignImage} />
      </Helmet>

      <Box
        bgGradient="linear(to-br, #fff6ef 0%, #f5f3ee 40%, #fefcf8 100%)"
        py={{ base: 16, md: 20 }}
        px={6}
      >
        <Center>
          <VStack spacing={8} maxW="1200px" w="full">
            <Box textAlign="center">
              <Badge
                colorScheme="orange"
                variant="subtle"
                px={4}
                py={1}
                borderRadius="full"
                textTransform="uppercase"
                letterSpacing="wide"
                fontWeight="bold"
              >
                Simple, transparent pricing
              </Badge>
              <Heading as="h1" size="2xl" mt={4} mb={4} color="#0f393b">
                Simple, Transparent Pricing
              </Heading>
              <Text fontSize="lg" maxW="680px" mx="auto" color="gray.600">
                All the features you need. Zero fees. Choose the plan that fits your organization.
              </Text>
              <Stack
                direction={{ base: 'column', sm: 'row' }}
                spacing={4}
                mt={8}
                justify="center"
              >
                <Button
                  colorScheme="orange"
                  size="lg"
                  as={Link}
                  to="/register"
                  px={8}
                >
                  Start fundraising
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  borderColor="#0f393b"
                  color="#0f393b"
                  as={Link}
                  to="/contact"
                  px={8}
                >
                  Talk to our team
                </Button>
              </Stack>
              <HStack spacing={6} mt={8} justify="center" color="gray.500" fontSize="sm">
                <HStack spacing={2}>
                  <Icon as={CheckCircleIcon} color="#f08453" />
                  <Text>Zero platform fees</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={CheckCircleIcon} color="#f08453" />
                  <Text>Unlimited campaigns</Text>
                </HStack>
                <HStack spacing={2}>
                  <Icon as={CheckCircleIcon} color="#f08453" />
                  <Text>Unlimited donors</Text>
                </HStack>
              </HStack>
            </Box>
          </VStack>
        </Center>
      </Box>

      <Center py={{ base: 12, md: 16 }} px={6}>
        <VStack spacing={10} maxW="1200px" w="full">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} w="full">
            {[
              {
                title: 'Basic',
                highlight: false,
                description: 'Perfect for new or smaller organizations.',
                points: [
                  'Zero Transaction Fees',
                  'Unlimited Donations',
                  'Basic Support',
                ],
              },
              {
                title: 'Plus',
                highlight: true,
                description: 'For growing nonprofits that want to scale giving.',
                points: [
                  'All Basic Features',
                  'Custom Donation Forms',
                  'Recurring Donations',
                  'Priority Support',
                ],
              },
              {
                title: 'Premium',
                highlight: false,
                description: 'Built for high-volume campaigns and events.',
                points: [
                  'All Plus Features',
                  'Donor Management',
                  'Event Ticketing',
                  'Dedicated Support',
                ],
              },
            ].map((card) => (
              <Box
                key={card.title}
                bg="white"
                p={8}
                borderRadius="2xl"
                shadow={card.highlight ? 'xl' : 'md'}
                border="1px"
                borderColor={card.highlight ? 'orange.200' : 'gray.100'}
                position="relative"
              >
                {card.highlight ? (
                  <Badge
                    colorScheme="orange"
                    position="absolute"
                    top={6}
                    right={6}
                    px={3}
                    py={1}
                    borderRadius="full"
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    Most popular
                  </Badge>
                ) : null}
                <Heading as="h3" size="lg" mb={2} color="#0f393b">
                  {card.title}
                </Heading>
                <Text color="gray.500" fontWeight="semibold">
                  $0
                </Text>
                <Text color="gray.500" fontSize="sm" mb={4}>
                  per month
                </Text>
                <Text color="gray.600" mb={6}>
                  {card.description}
                </Text>
                <Divider borderColor="orange.100" mb={6} />
                <List spacing={3}>
                  {card.points.map((point) => (
                    <ListItem key={point} display="flex" alignItems="start">
                      <ListIcon as={CheckCircleIcon} color="#f08453" mt={1} />
                      <Text>{point}</Text>
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </SimpleGrid>

          <Box
            bg="white"
            borderRadius="2xl"
            shadow="xl"
            p={{ base: 8, md: 10 }}
            w="full"
            border="1px"
            borderColor="orange.100"
          >
            <Flex
              direction={{ base: 'column', md: 'row' }}
              justify="space-between"
              align={{ base: 'flex-start', md: 'center' }}
              gap={8}
            >
              <Box>
                <Badge colorScheme="orange" fontSize="sm" mb={3}>
                  All Plans Include
                </Badge>
                <Heading as="h2" size="lg" color="#0f393b" mb={3}>
                  Zero platform fees, every feature unlocked
                </Heading>
                <Text color="gray.600" maxW="560px">
                  Keep every dollar raised. We fund the platform so your supporters fuel the mission
                  directly.
                </Text>
              </Box>
              <Box
                bg="#fef5ee"
                borderRadius="xl"
                p={6}
                minW={{ base: 'full', md: '320px' }}
              >
                <List spacing={3} fontSize="sm">
                  {[
                    'Zero Platform Fees',
                    'Unlimited Fundraising Campaigns',
                    'Access to All Features',
                    'Dedicated Support',
                    'Secure Payment Processing',
                    'And Much More!',
                  ].map((item) => (
                    <ListItem key={item} display="flex" alignItems="start">
                      <ListIcon as={CheckCircleIcon} color="#f08453" mt={1} />
                      <Text>{item}</Text>
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Flex>
          </Box>

          <Box w="full" textAlign="center" pt={4}>
            <Heading as="h2" size="lg" mb={4} color="#0f393b">
              Stay Dedicated to Your Cause. Leave the Fees Behind.
            </Heading>
            <Text color="gray.600" maxW="720px" mx="auto" mb={6}>
              Every Dollar Drives Change.
            </Text>
            <Stack direction={{ base: 'column', sm: 'row' }} spacing={4} justify="center">
              <Button
                colorScheme="orange"
                size="lg"
                as={Link}
                to="/register"
                px={8}
              >
                Create your free account
              </Button>
              <Button
                variant="outline"
                size="lg"
                borderColor="#0f393b"
                color="#0f393b"
                as={Link}
                to="/contact"
                px={8}
              >
                Schedule a demo
              </Button>
            </Stack>
          </Box>
        </VStack>
      </Center>
    </Box>
  );
};

export default Pricing;
